import React, { useState, useEffect } from "react";
import { Box, Typography, Grid, Container, CircularProgress, TextField, InputAdornment, IconButton } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { motion } from "framer-motion";
import Header from "../../Components/Common/Header";
import Footer from "../../Components/Common/Footer";
import { brandLogoMap } from "../../assets/dummyData";
import { fetchHomepageBrandsShowcase } from "../../redux/slices/brandsShowcaseSlice";

const ViewBrandsOnSaleNew = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const dispatch = useDispatch();

  const handleBack = () => {
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate("/");
    }
  };

  useEffect(() => {
    if (navigationType !== "POP") {
      window.scrollTo(0, 0);
    }
  }, [location.pathname, navigationType]);
  
  const [validBrands, setValidBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Determine if this is the "on-sale" or "new-arrivals" view all page
  const isSale = location.pathname.includes("on-sale");
  const pathPrefix = isSale ? "/on-sale" : "/new-arrivals";
  
  const headingText = isSale ? "Brands on Sale" : "Brands with New Arrivals";

  // Use the Redux state that was already fetched on the homepage
  const { onSale, loading: reduxLoading } = useSelector((state) => state.brandsShowcase);

  useEffect(() => {
    // If the user navigates directly to this page (e.g. refresh), Redux state will be empty.
    if (Object.keys(onSale || {}).length === 0 && !reduxLoading) {
      dispatch(fetchHomepageBrandsShowcase());
    }
  }, [dispatch, onSale, reduxLoading]);

  useEffect(() => {
    if (reduxLoading) {
      setLoading(true);
      return;
    }
    
    if (isSale) {
      // Extract brand names that have at least one product in their onSale array
      const brandsWithSales = Object.keys(onSale || {}).filter(
        (brandName) => onSale[brandName] && onSale[brandName].length > 0
      );
      
      // Filter our brandLogoMap to only show brands that actually have sales
      const sortedSalesBrands = brandLogoMap
        .filter(b => brandsWithSales.includes(b.name))
        .sort((a, b) => a.name.localeCompare(b.name));
      setValidBrands(sortedSalesBrands);
    } else {
      // For new arrivals, display all our core brands
      const sortedBrands = [...brandLogoMap].sort((a, b) => a.name.localeCompare(b.name));
      setValidBrands(sortedBrands);
    }
    setLoading(false);
  }, [isSale, onSale]);

  const displayedBrands = validBrands.filter(b => 
    b.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const containerVariants = {
    hidden: {},
    visible: {
      transition: { staggerChildren: 0.05 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 15 },
    visible: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 80, damping: 15 } }
  };

  const headerVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
  };

  return (
    <Box sx={{ background: "#FFFFFF", minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <Header />
      
      <Box sx={{ flex: 1, py: { xs: 6, md: 10 }, background: "linear-gradient(180deg, #FFFFFF 0%, #FAFAFA 50%, #FFFFFF 100%)" }}>
        <Container maxWidth="lg">
          <Box 
            component={motion.div}
            initial="hidden"
            animate="visible"
            variants={headerVariants}
            sx={{ mb: { xs: 6, md: 10 }, textAlign: "center", position: "relative" }}
          >
            <IconButton
              onClick={handleBack}
              sx={{
                position: "absolute",
                left: { xs: 0, md: 24 },
                top: { xs: 0, md: 12 },
                zIndex: 10,
                bgcolor: "rgba(0,0,0,0.02)",
                "&:hover": { bgcolor: "rgba(0,0,0,0.06)" },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography
              variant="h1"
              sx={{
                fontFamily: "'Lora', serif",
                fontSize: { xs: "36px", md: "52px" },
                color: "#111",
                mb: 2,
                fontWeight: 500,
                letterSpacing: "-0.02em",
                textTransform: "capitalize",
              }}
            >
              {isSale ? (
                <>Brands on <Box component="span" sx={{ fontStyle: "italic", color: "#FF6B00" }}>Sale</Box></>
              ) : (
                <>Brands with <Box component="span" sx={{ fontStyle: "italic", color: "#FF6B00" }}>New Arrivals</Box></>
              )}
            </Typography>

            <Box
              sx={{
                display: "inline-flex",
                alignItems: { xs: "flex-start", md: "center" },
                backgroundColor: "rgba(255, 107, 0, 0.06)",
                border: "1px solid rgba(255, 107, 0, 0.2)",
                borderRadius: { xs: "16px", md: "100px" },
                px: { xs: 2.5, md: 3 },
                py: { xs: 1, md: 1.5 },
                mx: "auto",
              }}
            >
              <Box sx={{ display: "flex", mt: { xs: "2px", md: 0 }, flexShrink: 0, mr: 1.5 }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#FF6B00" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                  <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
                </svg>
              </Box>
              <Typography
                sx={{
                  fontFamily: "Poppins-Medium",
                  fontSize: { xs: "13px", md: "15px" },
                  color: "#FF6B00",
                  letterSpacing: "0.5px",
                  textAlign: "center",
                }}
              >
                Select a brand to discover its {isSale ? "latest sales" : "freshest drops"}
              </Typography>
            </Box>

            {/* Live Search Bar */}
            <Box sx={{ maxWidth: "500px", mx: "auto", mt: 4, mb: 2 }}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Search brands..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "#999" }} />
                    </InputAdornment>
                  ),
                  sx: {
                    borderRadius: "100px",
                    backgroundColor: "#fff",
                    fontFamily: "Poppins-Regular",
                    fontSize: "15px",
                    "& fieldset": { borderColor: "rgba(0,0,0,0.1)" },
                    "&:hover fieldset": { borderColor: "rgba(0,0,0,0.2)" },
                    "&.Mui-focused fieldset": { borderColor: "#FF6B00", borderWidth: "1px" },
                  }
                }}
              />
            </Box>
          </Box>

          <Box sx={{ maxWidth: "1000px", mx: "auto" }}>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", width: "100%", py: 10 }}>
                <CircularProgress sx={{ color: "#FF6B00" }} />
              </Box>
            ) : (
              <Box
                key={searchQuery}
                component={motion.div}
                initial="hidden"
                animate="visible"
                variants={containerVariants}
              >
                {displayedBrands.length === 0 ? (
                  <Box sx={{ textAlign: "center", py: 10 }}>
                    <Typography sx={{ fontFamily: "Poppins-Medium", color: "#666", fontSize: "16px" }}>
                      No brands found matching "{searchQuery}"
                    </Typography>
                  </Box>
                ) : (
                  <Grid container spacing={{ xs: 2, md: 4 }}>
                    {displayedBrands.map((brand, idx) => (
                      <Grid item xs={12} sm={6} md={4} key={idx}>
                      <Box
                        component={motion.div}
                        variants={itemVariants}
                        onClick={() => {
                          const slug = brand.name.replace(/\s+/g, '-');
                          navigate(`${pathPrefix}/${encodeURIComponent(slug)}`);
                        }}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          py: 1.5,
                          borderBottom: "1px solid #EAEAEA",
                          cursor: "pointer",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            borderColor: "#FF6B00",
                          },
                          "&:hover .brand-text": {
                            color: "#FF6B00",
                            transform: "translateX(8px)"
                          },
                          "&:hover .brand-icon": {
                            opacity: 1,
                            transform: "translateX(-4px)"
                          }
                        }}
                      >
                        <Typography
                          className="brand-text"
                          sx={{
                            fontFamily: "Poppins-Medium",
                            fontSize: { xs: "14px", md: "15px" },
                            color: "#444",
                            transition: "all 0.3s ease",
                          }}
                        >
                          {brand.name}
                        </Typography>
                        <svg className="brand-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#FF6B00" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0, transform: "translateX(-16px)", transition: "all 0.3s ease" }}>
                          <line x1="5" y1="12" x2="19" y2="12"></line>
                          <polyline points="12 5 19 12 12 19"></polyline>
                        </svg>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
                )}
              </Box>
            )}
            
            {!loading && validBrands.length === 0 && (
              <Box sx={{ textAlign: "center", py: 10 }}>
                <Typography sx={{ fontFamily: "Poppins-Medium", color: "#666", fontSize: "18px" }}>
                  No brands found.
                </Typography>
              </Box>
            )}
          </Box>
        </Container>
      </Box>
      
      <Footer />
    </Box>
  );
};

export default ViewBrandsOnSaleNew;
